import React, { memo, useMemo } from 'react';
import { Eye, BadgeCheck, Building2 } from 'lucide-react';

// Island flag emojis for compact display
const ISLAND_FLAGS = {
  aruba: '🇦🇼',
  curacao: '🇨🇼',
  bonaire: '🇧🇶',
};

/**
 * ListingCardHeader
 * Displays listing title with fixed height for consistent card alignment
 */
const ListingCardHeader = memo(({
  listing,
  compact = false
}) => {
  const isVerified = listing.is_verified || listing.is_verified_business;
  
  return (
    <div className="flex-1">
      {/* Title - single line, truncated */}
      <h3 
        className={`font-semibold text-foreground line-clamp-1 leading-tight ${
          compact ? 'text-xs sm:text-sm' : 'text-sm'
        } group-hover:text-primary transition-colors flex items-center gap-1`}
        data-testid={`listing-title-${listing.id}`}
      >
        <span className="flex-1 truncate">{listing.title}</span>
        {isVerified && (
          <BadgeCheck 
            className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-blue-500 flex-shrink-0`}
            title="Verified Seller" 
          />
        )}
      </h3>
      
      {/* Yard Sale item count removed - now shown in Price component */}
    </div>
  );
});

ListingCardHeader.displayName = 'ListingCardHeader';

/**
 * ListingCardFooter
 * Compact footer with seller info, island flag, and views on one line
 */
export const ListingCardFooter = memo(({
  listing,
  compact = false,
  currentIsland = null
}) => {
  // Get island flag emoji - prioritize current island context
  const islandFlag = useMemo(() => {
    const visibleIslands = listing.visible_islands || [];
    
    // If current island is set and listing is visible there, show that flag
    if (currentIsland && visibleIslands.includes(currentIsland)) {
      return ISLAND_FLAGS[currentIsland] || '🌴';
    }
    
    // Otherwise show the first island flag
    if (visibleIslands.length > 0) {
      return ISLAND_FLAGS[visibleIslands[0]] || '🌴';
    }
    
    // Fallback to palm tree for legacy listings without visible_islands
    return '🌴';
  }, [listing.visible_islands, currentIsland]);
  
  const isVerified = listing.is_verified || listing.is_verified_business;
  
  return (
    <div className="mt-auto pt-2 border-t border-border">
      {/* Single row: Seller + Island flag + Views */}
      <div className="flex items-center gap-2">
        {/* Seller Avatar - compact w-6 */}
        {listing.user_name && (
          <>
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-cyan-500 flex items-center justify-center text-[10px] font-semibold text-white overflow-hidden flex-shrink-0">
              {listing.user_avatar ? (
                <img 
                  src={listing.user_avatar} 
                  alt={listing.user_name} 
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                listing.user_name?.charAt(0).toUpperCase()
              )}
            </div>
            
            {/* Business icon (if applicable) */}
            {(listing.user_account_type === 'business' || listing.is_verified_business) && (
              <Building2 
                className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" 
                title="Zakelijk account"
              />
            )}
            
            {/* Seller name (truncates) */}
            <span 
              className="text-[11px] font-medium text-muted-foreground truncate flex-1 min-w-0"
              data-testid={`listing-seller-${listing.id}`}
            >
              {listing.user_name}
            </span>
            
            {/* Verified badge */}
            {isVerified && (
              <BadgeCheck 
                className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0"
                title="Geverifieerd" 
              />
            )}
          </>
        )}
        
        {/* Island flag + Views - right aligned */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto text-muted-foreground">
          {/* Island flag - hidden on mobile */}
          <span className="hidden sm:inline text-sm" title={
            currentIsland && listing.visible_islands?.includes(currentIsland) 
              ? currentIsland.charAt(0).toUpperCase() + currentIsland.slice(1)
              : listing.visible_islands?.[0]?.charAt(0).toUpperCase() + listing.visible_islands?.[0]?.slice(1) || 'Caribbean'
          }>
            {islandFlag}
          </span>
          <div className="flex items-center gap-0.5 text-[11px]">
            <Eye className="w-3 h-3" />
            <span data-testid={`listing-views-${listing.id}`}>
              {listing.views || listing.view_count || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

ListingCardFooter.displayName = 'ListingCardFooter';

export default ListingCardHeader;
